import { FilterItemViewModel, FilterViewModel } from '@/shared/models/view/filter-viewmodel';
import { IDictionary } from '../models/interface/dictionary-model';
import HttpService from './http-service';

export interface IAzureSearchFacet {
    count: number,
    value: string
}

export interface IAzureSearchResponse<T> {
    "@odata.context": string;
    "@odata.count": number;
    "@search.facets": IDictionary<IAzureSearchFacet[]>;
    value: T[];
}

export interface IAzureSearchService {
    readonly index: string;
    readonly path: string;
    readonly host?: string;
    readonly version: string;

    url: string;
    header: string;

    search(query: Query): Promise<any>;
}

export abstract class AzureSearchService implements IAzureSearchService {
    readonly abstract index: string;
    readonly abstract path: string;
    readonly host?: string | undefined;
    readonly version: string = "2020-06-30";

    constructor() { }

    get url(): string {
        return `${this.host || ""}${this.path}`;
    }

    get header(): any {
        return {
            "index": this.index,
            "api-version": this.version
        };
    }

    public search(query: Query): Promise<any> {
        return HttpService.post(
            `${this.host || ""}${this.path}`,
            query,
            this.header
        );
    }
}

export class VUwebSearchService extends AzureSearchService {
    readonly path: string = "/api/search";

    constructor(readonly index: string, readonly host?: string) {
        super();
    }
}

export class VUwebPeopleSearchService extends VUwebSearchService {
    readonly path: string = "/api/search/people";
}

export class PureAzureSearchService extends AzureSearchService {
    readonly index: string = 'pure-data-index';
    readonly path: string = "/puredata/search";

    constructor(readonly host: string) {
        super();
    }
}

export class Query {
    private filter?: string;
    private facets?: string[]; // ["ItemType", "Language", "StudyType", "ServiceCategories"];
    private search?: string;
    private top?: number;
    private skip?: number;
    private count?: boolean;
    private searchFields?: string;
    private highlight?: string;
    private highlightPreTag?: string;
    private highlightPostTag?: string;
    private orderby?: string;
    private scoringProfile?: string;
    private queryType?: string;

    constructor(){
        this.queryType = "full";
    }

    // filter: string = '' //"odata_filter_expression"
    public SetFilter(filter: string): Query {
        this.filter = filter;
        return this;
    }

    public AddFilter(filter: string): Query {
        this.filter = filter ? `${this.filter} and ${filter}` : this.filter;
        return this;
    }

    // facets": [ "facet_expression_1", "facet_expression_2", ... ]
    public SetFacets(facets: string[]): Query {
        this.facets = facets;
        return this;
    }

    // search": "simple_query_expression",
    public SetSearch(search: string): Query {
        search = search?.replaceAll('"', '');

        if (search) {
            search = this.escapeSpecialChars(search);
        }

        if (search) {
            this.search = this.formatQuery(search);
        }
        else {
            this.search = '*';
        }

        return this;
    }

    private escapeSpecialChars(input: string): string {
        const specialChars = "+-&|!(){}[]^\"~*?:\\/";
        let output = "";

        for (let i = 0; i < input.length; i++) {
            const char = input.charAt(i);
            if (specialChars.includes(char)) {
                output += `\\${char}`;
            } else {
                output += char;
            }
        }

        return output;
    }

    private formatQuery(input: string): string {
        const searchTerms = input
            .split(/[\s+]+/)
            .filter(term => term.trim() !== '');

        let searchQuery: string;

        if (searchTerms.length === 0) {
            searchQuery = '*';
        } else if (searchTerms.length === 1) {
            searchQuery = `"${input}" OR ${input}*`;
        } else {
            searchQuery = `"${input}" OR (`;
            for (let i = 0; i < searchTerms.length; i++) {
                searchQuery += `${searchTerms[i]}*`;
                if (i !== searchTerms.length - 1) {
                    searchQuery += " AND ";
                }
            }
            searchQuery += ')';
        }

        return searchQuery;
    }

    // top": #
    public SetTop(top: number): Query {
        this.top = top;
        return this;
    }

    //   skip": # (default 0)
    public SetSkip(skip: number): Query {
        this.skip = skip;
        return this;
    }

    //   count: boolean
    public SetCount(count: boolean): Query {
        this.count = count;
        return this;
    }

    //   highlight": "highlight_field_1, highlight_field_2, ...",
    public SetHighlight(fields: string) {
        this.highlight = fields;
        return this;
    }

    //   orderby": "orderby_expression",
    public SetOrderBy(expression: string) {
        this.orderby = expression;
        return this
    }

    //   scoringProfile": "scoring_profile_name",
    public SetScoringProfile(name: string) {
        this.scoringProfile = name;
        return this;
    }

    //   highlightPreTag": "pre_tag",
    //   highlightPostTag": "post_tag",
    public SetHighlightTag(startTag: string, endTag: string) {
        this.highlightPreTag = startTag;
        this.highlightPostTag = endTag;
        return this;
    }

    //   minimumCoverage": # (% of index that must be covered to declare query successful; default 100),
    //   scoringParameters": [ "scoring_parameter_1", "scoring_parameter_2", ... ],
    //   searchMode": "any" (default) | "all",
    //   select": "field_name_1, field_name_2, ...",
    //   searchFields": "field_name_1, field_name_2, ...",
}

export class FilterBuilder {
    public filter: string = '';

    public addFilter(facet: FilterViewModel) {
        if (facet.values.length) {
            this.filter = this.filter ? `${this.filter} and ${this.createFilter(facet)}` : this.createFilter(facet)
        }

        return this;
    }

    private createFilter(facet: FilterViewModel) {
        let newOr = '';
        facet.values.forEach((item: FilterItemViewModel) => {
            var syntax = facet.single
                ? this.CreateQuerySingleSyntax(facet.name, item.name)
                : this.CreateQueryListSyntax(facet.name, item.name);
            newOr = newOr ? `${newOr} or ${syntax}` : syntax;
        });
        return `(${newOr})`;
    }

    private CreateQuerySingleSyntax(facet: string, value: string) {
        return `${facet} eq '${value}'`;
    }

    private CreateQueryListSyntax(facet: string, value: string) {
        return `${facet}/any(c: c eq '${value}')`;
    }
}
